import { Security } from '../../../proto/generated/security'
import { TechnicalsTimeframeConfig } from '../../../proto/generated/overview-widget'
import { getSecurityKey } from '../../../util/security'
import { fetchTechnicalsCandles } from './fetchTechnicalsCandles'
import { CANDLE_CACHE_TTL_MS, TechnicalsCandleSeries } from './types'

type CacheEntry = {
    fetchedAt: number
    series: TechnicalsCandleSeries
}

const cache = new Map<string, CacheEntry>()
const inFlight = new Map<string, Promise<TechnicalsCandleSeries>>()

const cacheKey = (security: Security, timeframeId: string) =>
    `${getSecurityKey(security.exchange, security.code)}:${timeframeId}`

export const getTechnicalsCandlesCached = async (
    security: Security,
    timeframe: TechnicalsTimeframeConfig,
    signal?: AbortSignal
): Promise<TechnicalsCandleSeries> => {
    const key = cacheKey(security, timeframe.id)
    const existing = cache.get(key)
    if (existing && Date.now() - existing.fetchedAt < CANDLE_CACHE_TTL_MS) {
        return existing.series
    }

    // Shared fetch is not abortable — one caller's abort must not cancel others / waste the page.
    let pending = inFlight.get(key)
    if (!pending) {
        pending = fetchTechnicalsCandles(security, timeframe)
            .then((series) => {
                cache.set(key, { fetchedAt: Date.now(), series })
                return series
            })
            .finally(() => {
                inFlight.delete(key)
            })
        inFlight.set(key, pending)
    }

    const series = await pending
    if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError')
    }
    return series
}
